import type { ChatMessage } from './api'

/** Polyfill for crypto.randomUUID — not available in non-secure contexts (HTTP) */
function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().slice(0, 8)
  }
  // Fallback for HTTP / older WebViews
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 6)
}

export interface ChatSession {
  id: string           // generateId() — 8-char unique
  title: string        // First 50 chars of first user message, trimmed
  modelId: string
  modelName: string
  messages: ChatMessage[]
  createdAt: number    // Date.now()
  updatedAt: number    // Date.now() on every save
}

// Active storage key — changes when wallet connects/disconnects
let activeKey = 'imago:chats:guest'

const MAX_SESSIONS = 50
const MAX_SESSION_BYTES = 200_000  // 200KB per session safety cap

/** Guard against corrupted or legacy localStorage data */
function isValidSession(s: unknown): s is ChatSession {
  if (!s || typeof s !== 'object') return false
  const o = s as Record<string, unknown>
  return (
    typeof o.id === 'string' && o.id.length > 0 &&
    typeof o.title === 'string' &&
    typeof o.modelId === 'string' &&
    typeof o.modelName === 'string' &&
    Array.isArray(o.messages) &&
    typeof o.createdAt === 'number' &&
    typeof o.updatedAt === 'number'
  )
}

function load(): ChatSession[] {
  try {
    const raw = JSON.parse(localStorage.getItem(activeKey) ?? '[]') as unknown[]
    return Array.isArray(raw) ? raw.filter(isValidSession) : []
  } catch {
    return []
  }
}

function persist(sessions: ChatSession[]): void {
  try {
    localStorage.setItem(activeKey, JSON.stringify(sessions))
  } catch {
    // localStorage quota exceeded — drop oldest half
    const half = sessions.slice(0, Math.floor(sessions.length / 2))
    try { localStorage.setItem(activeKey, JSON.stringify(half)) } catch { /* give up */ }
  }
}

export const chatStorage = {
  /**
   * Switch to wallet-specific storage. Call on wallet connect/disconnect.
   * Pass null or undefined to reset to guest storage.
   */
  setWallet(tonAddress: string | null | undefined): void {
    activeKey = tonAddress ? `imago:chats:${tonAddress}` : 'imago:chats:guest'
  },

  list: (): ChatSession[] =>
    load().sort((a, b) => b.updatedAt - a.updatedAt),

  get: (id: string): ChatSession | null =>
    load().find((s) => s.id === id) ?? null,

  create: (modelId: string, modelName: string): ChatSession => {
    const session: ChatSession = {
      id: generateId(),
      title: 'New Chat',
      modelId,
      modelName,
      messages: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    }
    const all = load()
    persist([session, ...all].slice(0, MAX_SESSIONS))
    return session
  },

  save: (session: ChatSession): void => {
    // Safety: don't persist huge sessions
    let toSave = session
    const size = JSON.stringify(session).length
    if (size > MAX_SESSION_BYTES) {
      toSave = { ...session, messages: session.messages.slice(-20) }
    }
    const all = load().filter((s) => s.id !== session.id)
    persist([{ ...toSave, updatedAt: Date.now() }, ...all].slice(0, MAX_SESSIONS))
  },

  delete: (id: string): void => {
    persist(load().filter((s) => s.id !== id))
  },

  updateTitle: (id: string, title: string): void => {
    const all = load()
    const idx = all.findIndex((s) => s.id === id)
    if (idx === -1) return
    all[idx] = { ...all[idx], title, updatedAt: Date.now() }
    persist(all)
  },
}
